import * as React from "react";

import { Plus as PlusIcon } from "@/shared/foundation/icons";
import {
  RobotoButton,
  type RobotoButtonProps,
} from "@/shared/foundation/primitives/RobotoButton";

type ButtonProps = Omit<
  Extract<RobotoButtonProps, { children: React.ReactNode }>,
  "children" | "icon" | "variant" | "size" | "className"
>;

export interface CreateEntityButtonProps extends ButtonProps {
  /** The record type in sentence case, as it follows "New": "device type". */
  entity: string;
  /**
   * `primary` in a list page's toolbar; `secondary` in the list's empty state.
   * @default "primary"
   */
  variant?: "primary" | "secondary";
}

/**
 * A list page's "New <entity>" button: the same label, style and Plus icon on
 * every list, so creating any kind of record starts from the same control.
 * Place it in the list's toolbar `actions`, and with `variant="secondary"` in
 * its empty state. It forwards its ref and any other props to the button, so
 * a popover can use it as its trigger.
 *
 * The label is one string, not JSX, so RobotoButton can also name the button
 * with it when a narrow toolbar hides the label and shows only the icon.
 */
export const CreateEntityButton = React.forwardRef<
  HTMLButtonElement,
  CreateEntityButtonProps
>(function CreateEntityButton({ entity, variant = "primary", ...props }, ref) {
  return (
    <RobotoButton
      {...props}
      ref={ref}
      variant={variant}
      icon={<PlusIcon aria-hidden="true" />}
    >
      {`New ${entity}`}
    </RobotoButton>
  );
});
